/**
 * Keeps native Shopify facet options visible after AJAX filtering.
 * Shopify omits zero-result values from filter.values on section refresh.
 */
(function () {
  const STORAGE_PREFIX = "gh-facet-catalog:";

  function storageKey() {
    return STORAGE_PREFIX + window.location.pathname;
  }

  function loadCatalog() {
    try {
      return JSON.parse(sessionStorage.getItem(storageKey()) || "{}");
    } catch {
      return {};
    }
  }

  function saveCatalog(catalog) {
    try {
      sessionStorage.setItem(storageKey(), JSON.stringify(catalog));
    } catch {
      /* ignore quota errors */
    }
  }

  function extractLabel(text) {
    return (text || "").replace(/\s*\(\d+\)\s*$/, "").trim();
  }

  function mergeEntries(catalog, paramName, entries) {
    if (!paramName || !entries?.length) return;

    if (!catalog[paramName]) {
      catalog[paramName] = [];
    }

    entries.forEach((entry) => {
      if (!entry?.value) return;

      const index = catalog[paramName].findIndex((item) => item.value === entry.value);

      if (index >= 0) {
        catalog[paramName][index] = {
          label: entry.label || catalog[paramName][index].label,
          value: entry.value,
        };
      } else {
        catalog[paramName].push({
          label: entry.label || entry.value,
          value: entry.value,
        });
      }
    });
  }

  function parseCatalogSeed(text) {
    try {
      return JSON.parse(text || "{}");
    } catch {
      return {};
    }
  }

  function seedFromJson(form, catalog) {
    form.querySelectorAll("script[data-gh-facet-catalog]").forEach((script) => {
      const seed = parseCatalogSeed(script.textContent);

      Object.entries(seed).forEach(([paramName, entries]) => {
        mergeEntries(catalog, paramName, entries);
      });
    });
  }

  function captureFromList(list, catalog) {
    const paramName = list.dataset.facetParam;

    if (!paramName) return;

    const entries = [];

    list.querySelectorAll(".checkbox-container").forEach((container) => {
      const input = container.querySelector('input.checkbox[type="checkbox"]');

      if (!input || !input.name || input.name.includes("-disabled-")) return;

      const label = extractLabel(container.querySelector("label")?.textContent);

      entries.push({ label: label || input.value, value: input.value });
    });

    list.querySelectorAll('input.sr-only[type="checkbox"][name]').forEach((input) => {
      if (input.name.includes("-disabled-")) return;

      const labelEl = list.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      const label = extractLabel(labelEl?.textContent);

      entries.push({ label: label || input.value, value: input.value });
    });

    mergeEntries(catalog, paramName, entries);
  }

  function captureFromForm(form, catalog) {
    form.querySelectorAll("[data-gh-persist-facets]").forEach((list) => {
      captureFromList(list, catalog);
    });
  }

  function optionExists(list, value) {
    return Array.from(list.querySelectorAll('input[type="checkbox"]')).some((input) => input.value === value);
  }

  function createDisabledCheckbox(entry, idPrefix) {
    const container = document.createElement("div");
    container.className = "checkbox-container";
    container.dataset.persistentFacet = "true";

    const id = `${idPrefix}-${entry.value}`.replace(/[^a-zA-Z0-9-_]/g, "-");
    const input = document.createElement("input");

    input.id = id;
    input.className = "checkbox";
    input.type = "checkbox";
    input.disabled = true;
    input.value = entry.value;

    const label = document.createElement("label");

    label.htmlFor = id;
    label.textContent = `${entry.label} (0)`;

    container.appendChild(input);
    container.appendChild(label);

    return container;
  }

  function restoreToList(list, catalog) {
    const paramName = list.dataset.facetParam;
    const entries = catalog[paramName];

    if (!paramName || !entries?.length) return;

    const idPrefix = `checkbox-persist-${paramName}`;

    entries.forEach((entry) => {
      if (optionExists(list, entry.value)) return;

      list.appendChild(createDisabledCheckbox(entry, idPrefix));
    });
  }

  function restoreToForm(form, catalog) {
    form.querySelectorAll("[data-gh-persist-facets]").forEach((list) => {
      restoreToList(list, catalog);
    });
  }

  function processFacetForm(form) {
    if (!form) return;

    const catalog = loadCatalog();

    seedFromJson(form, catalog);
    captureFromForm(form, catalog);
    restoreToForm(form, catalog);
    saveCatalog(catalog);
  }

  function processAllFacetForms(root = document) {
    const scope = root && root.querySelectorAll ? root : document;
    const forms = scope.querySelectorAll
      ? scope.querySelectorAll('form#facet-form, form[is="facet-form"]')
      : [];

    forms.forEach((form) => {
      if (form.closest(".collection, .shopify-section, facet-drawer")) {
        processFacetForm(form);
      }
    });
  }

  async function seedCatalogFromUnfiltered() {
    const form = document.querySelector('form#facet-form[is="facet-form"], form#facet-form');

    if (!form || window.__ghFacetCatalogSeeded) return;

    const sectionId = form.getAttribute("section-id");

    if (!sectionId) return;

    const url = new URL(window.location.href);

    [...url.searchParams.keys()]
      .filter((key) => key.startsWith("filter.") || key === "page")
      .forEach((key) => url.searchParams.delete(key));

    url.searchParams.set("section_id", sectionId);

    try {
      const response = await fetch(url.toString(), { credentials: "same-origin" });
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const remoteForm = doc.querySelector('form#facet-form, form[is="facet-form"]');

      if (!remoteForm) return;

      const catalog = loadCatalog();

      seedFromJson(remoteForm, catalog);
      captureFromForm(remoteForm, catalog);
      saveCatalog(catalog);
      window.__ghFacetCatalogSeeded = true;
      processAllFacetForms();
    } catch {
      /* ignore network errors */
    }
  }

  window.GHFacetPersist = {
    processAllFacetForms,
    seedCatalogFromUnfiltered,
  };

  document.addEventListener("facet:updated", (event) => {
    processAllFacetForms(event.detail?.section || document);
  });

  function init() {
    processAllFacetForms();
    seedCatalogFromUnfiltered();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
